import axios from 'axios';
import { cachedRequest, requestCache } from '../utils/requestCache';
import { apiDebugger } from '../utils/apiDebugger';

// Determine API base URL. Prefer explicit env var.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Token management for JWT
export const getAccessToken = () => localStorage.getItem('accessToken');
export const getRefreshToken = () => localStorage.getItem('refreshToken');
export const setTokens = (accessToken, refreshToken) => {
  if (accessToken) localStorage.setItem('accessToken', accessToken);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
};
export const clearTokens = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('authToken'); // Clear legacy token too
};

// Legacy token support (for backward compatibility)
export const getAuthToken = () => localStorage.getItem('authToken');
export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Attach token on each request
client.interceptors.request.use((config) => {
  // Try JWT first, then fall back to legacy token
  const accessToken = getAccessToken();
  const legacyToken = getAuthToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else if (legacyToken) {
    config.headers.Authorization = `Token ${legacyToken}`;
  }

  // Debug API calls
  apiDebugger.logCall(`${config.method?.toUpperCase()} ${config.url}`, 'axios-interceptor');

  return config;
});

// Handle 401 errors and refresh token automatically
client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return client(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();
      if (!refreshToken) {
        // No refresh token, logout
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const response = await axios.post(`${API_BASE_URL}/api/token/refresh/`, {
          refresh: refreshToken,
        });

        const { access } = response.data;
        setTokens(access, refreshToken);

        // Update authorization header
        originalRequest.headers.Authorization = `Bearer ${access}`;

        // Process queued requests
        processQueue(null, access);
        isRefreshing = false;

        // Retry original request
        return client(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout
        processQueue(refreshError, null);
        isRefreshing = false;
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default client;

// API helpers
export const AuthAPI = {
  // JWT login
  loginJWT: async (username, password) => {
    apiDebugger.logCall('POST /api/token/', 'AuthAPI.loginJWT');
    const resp = await axios.post(`${API_BASE_URL}/api/token/`, { username, password });
    return resp.data; // { access, refresh }
  },
  // Legacy token login (for backward compatibility)
  login: async (username, password) => {
    apiDebugger.logCall('POST /api/auth-token/', 'AuthAPI.login');
    const resp = await axios.post(`${API_BASE_URL}/api/auth-token/`, { username, password });
    return resp.data; // { token }
  },
  refreshToken: async (refreshToken) => {
    apiDebugger.logCall('POST /api/token/refresh/', 'AuthAPI.refreshToken');
    const resp = await axios.post(`${API_BASE_URL}/api/token/refresh/`, { refresh: refreshToken });
    return resp.data; // { access }
  },
  verifyToken: async (token) => {
    apiDebugger.logCall('POST /api/token/verify/', 'AuthAPI.verifyToken');
    const resp = await axios.post(`${API_BASE_URL}/api/token/verify/`, { token });
    return resp.data;
  },
  me: async () => {
    apiDebugger.logCall('GET /api/users/me/', 'AuthAPI.me');
    const cacheKey = requestCache.generateKey('/api/users/me/', {});
    return cachedRequest(
      () => client.get('/api/users/me/').then(resp => resp.data),
      cacheKey,
      10 * 60 * 1000 // 10 minutes cache for user data
    );
  },
  logout: () => {
    clearTokens();
    requestCache.clear(); // Clear all cached requests on logout
  },
};

export const JobsAPI = {
  list: async (params = {}) => {
    const resp = await client.get('/api/biomarker/inference-jobs/', { params: { ...params, version: 'V2' } });
    return resp.data; // paginated data or list depending on router
  },
  retrieve: async (id) => {
    const resp = await client.get(`/api/biomarker/inference-jobs/${id}/`);
    return resp.data;
  },
  create: async (payload) => {
    payload.version = 'V2';
    const resp = await client.post('/api/biomarker/inference-jobs/', payload);
    return resp.data;
  },
};

export const FeatureExtractionAPI = {
  list: async (params = {}) => {
    const resp = await client.get('/api/biomarker/feature-extraction-jobs/', { params });
    return resp.data;
  },
  retrieve: async (id) => {
    const resp = await client.get(`/api/biomarker/feature-extraction-jobs/${id}/`);
    return resp.data;
  },
  create: async (payload) => {
    const resp = await client.post('/api/biomarker/feature-extraction-jobs/', payload);
    return resp.data;
  },
  update: async (id, payload) => {
    const resp = await client.put(`/api/biomarker/feature-extraction-jobs/${id}/`, payload);
    return resp.data;
  },
  ready: async () => {
    const resp = await client.get('/api/biomarker/feature-extraction-jobs/ready/');
    return resp.data;
  },
};

export const MetaDataAPI = {
  fields: async (params = {}) => {
    apiDebugger.logCall('GET /api/ingestion/d-folders/metadata-fields/', 'MetaDataAPI.fields');
    const cacheKey = requestCache.generateKey('/api/ingestion/d-folders/metadata-fields/', params);
    return cachedRequest(
      () => client.get('/api/ingestion/d-folders/metadata-fields/', { params }).then(resp => resp.data),
      cacheKey,
      5 * 60 * 1000 // 5 minutes cache
    );
  },
  distinct: async (params = {}) => {
    apiDebugger.logCall('GET /api/ingestion/d-folders/metadata-distinct/', 'MetaDataAPI.distinct');
    const cacheKey = requestCache.generateKey('/api/ingestion/d-folders/metadata-distinct/', params);
    return cachedRequest(
      () => client.get('/api/ingestion/d-folders/metadata-distinct/', { params }).then(resp => resp.data),
      cacheKey,
      5 * 60 * 1000 // 5 minutes cache
    );
  },
};

// D-Folders API
export const DFoldersAPI = {
  list: async (params = {}) => {
    apiDebugger.logCall('GET /api/ingestion/d-folders/', 'DFoldersAPI.list');
    const resp = await client.get('/api/ingestion/d-folders/', { params });
    return resp.data;
  },
  retrieve: async (id) => {
    apiDebugger.logCall(`GET /api/ingestion/d-folders/${id}/`, 'DFoldersAPI.retrieve');
    const resp = await client.get(`/api/ingestion/d-folders/${id}/`);
    return resp.data;
  },
  byStudy: async () => {
    apiDebugger.logCall('GET /api/ingestion/d-folders/by-study/', 'DFoldersAPI.byStudy');
    const resp = await client.get('/api/ingestion/d-folders/by-study/');
    return resp.data;
  },
  exportExcel: async (params = {}) => {
    apiDebugger.logCall('GET /api/ingestion/d-folders/export-excel/', 'DFoldersAPI.exportExcel');
    const resp = await client.get('/api/ingestion/d-folders/export-excel/', {
      params, // params can include 'format' (xlsx or csv), 'study', etc.
      responseType: 'blob', // Important for file download
    });
    return resp;
  },
  download: async (id) => {
    apiDebugger.logCall(`GET /api/ingestion/d-folders/${id}/download/`, 'DFoldersAPI.download');
    const resp = await client.get(`/api/ingestion/d-folders/${id}/download/`);
    return resp.data;
  },
};

// Studies API
export const StudiesAPI = {
  list: async (params = {}) => {
    apiDebugger.logCall('GET /api/ingestion/studies/', 'StudiesAPI.list');
    const cacheKey = requestCache.generateKey('/api/ingestion/studies/', params);
    return cachedRequest(
      () => client.get('/api/ingestion/studies/', { params }).then(resp => resp.data),
      cacheKey,
      2 * 60 * 1000 // 2 minutes cache
    );
  },
  retrieve: async (id) => {
    apiDebugger.logCall(`GET /api/ingestion/studies/${id}/`, 'StudiesAPI.retrieve');
    const resp = await client.get(`/api/ingestion/studies/${id}/`);
    return resp.data;
  },
  dFolders: async (id) => {
    apiDebugger.logCall(`GET /api/ingestion/studies/${id}/d_folders/`, 'StudiesAPI.dFolders');
    const resp = await client.get(`/api/ingestion/studies/${id}/d_folders/`);
    return resp.data;
  },
  users: async (id) => {
    apiDebugger.logCall(`GET /api/ingestion/studies/${id}/users/`, 'StudiesAPI.users');
    const resp = await client.get(`/api/ingestion/studies/${id}/users/`);
    return resp.data;
  },
  create: async (data) => {
    apiDebugger.logCall('POST /api/ingestion/studies/', 'StudiesAPI.create');
    const resp = await client.post('/api/ingestion/studies/', data);
    requestCache.clear(); // Clear cache after mutation
    return resp.data;
  },
  update: async (id, data) => {
    apiDebugger.logCall(`PUT /api/ingestion/studies/${id}/`, 'StudiesAPI.update');
    const resp = await client.put(`/api/ingestion/studies/${id}/`, data);
    requestCache.clear(); // Clear cache after mutation
    return resp.data;
  },
};

// Download Jobs API
export const DownloadJobsAPI = {
  list: async (params = {}) => {
    apiDebugger.logCall('GET /api/ingestion/download-jobs/', 'DownloadJobsAPI.list');
    const resp = await client.get('/api/ingestion/download-jobs/', { params });
    return resp.data;
  },
  retrieve: async (id) => {
    apiDebugger.logCall(`GET /api/ingestion/download-jobs/${id}/`, 'DownloadJobsAPI.retrieve');
    const resp = await client.get(`/api/ingestion/download-jobs/${id}/`);
    return resp.data;
  },
  create: async (folderIds) => {
    apiDebugger.logCall('POST /api/ingestion/download-jobs/', 'DownloadJobsAPI.create');
    const resp = await client.post('/api/ingestion/download-jobs/', { folder_ids: folderIds });
    return resp.data;
  },
};

// User Study Permissions API
export const UserStudyPermissionsAPI = {
  list: async (params = {}) => {
    apiDebugger.logCall('GET /api/ingestion/user-study-permissions/', 'UserStudyPermissionsAPI.list');
    const resp = await client.get('/api/ingestion/user-study-permissions/', { params });
    return resp.data;
  },
  myPermissions: async () => {
    apiDebugger.logCall('GET /api/ingestion/user-study-permissions/my-permissions/', 'UserStudyPermissionsAPI.myPermissions');
    const cacheKey = requestCache.generateKey('/api/ingestion/user-study-permissions/my-permissions/', {});
    return cachedRequest(
      () => client.get('/api/ingestion/user-study-permissions/my-permissions/').then(resp => resp.data),
      cacheKey,
      5 * 60 * 1000 // 5 minutes cache
    );
  },
  create: async (data) => {
    apiDebugger.logCall('POST /api/ingestion/user-study-permissions/', 'UserStudyPermissionsAPI.create');
    const resp = await client.post('/api/ingestion/user-study-permissions/', data);
    requestCache.clear(); // Clear cache after mutation
    return resp.data;
  },
  update: async (id, data) => {
    apiDebugger.logCall(`PUT /api/ingestion/user-study-permissions/${id}/`, 'UserStudyPermissionsAPI.update');
    const resp = await client.put(`/api/ingestion/user-study-permissions/${id}/`, data);
    requestCache.clear(); // Clear cache after mutation
    return resp.data;
  },
  delete: async (id) => {
    apiDebugger.logCall(`DELETE /api/ingestion/user-study-permissions/${id}/`, 'UserStudyPermissionsAPI.delete');
    const resp = await client.delete(`/api/ingestion/user-study-permissions/${id}/`);
    requestCache.clear(); // Clear cache after mutation
    return resp.data;
  },
};

// Admin API - User Management
export const AdminUsersAPI = {
  list: async (params = {}) => {
    apiDebugger.logCall('GET /api/admin/users/', 'AdminUsersAPI.list');
    const resp = await client.get('/api/admin/users/', { params });
    return resp.data;
  },
  retrieve: async (id) => {
    apiDebugger.logCall(`GET /api/admin/users/${id}/`, 'AdminUsersAPI.retrieve');
    const resp = await client.get(`/api/admin/users/${id}/`);
    return resp.data;
  },
  create: async (data) => {
    apiDebugger.logCall('POST /api/admin/users/', 'AdminUsersAPI.create');
    const resp = await client.post('/api/admin/users/', data);
    return resp.data;
  },
  update: async (id, data) => {
    apiDebugger.logCall(`PUT /api/admin/users/${id}/`, 'AdminUsersAPI.update');
    const resp = await client.put(`/api/admin/users/${id}/`, data);
    return resp.data;
  },
  apiKeys: async (id) => {
    apiDebugger.logCall(`GET /api/admin/users/${id}/api-keys/`, 'AdminUsersAPI.apiKeys');
    const resp = await client.get(`/api/admin/users/${id}/api-keys/`);
    return resp.data;
  },
  createApiKey: async (id, data) => {
    apiDebugger.logCall(`POST /api/admin/users/${id}/api-keys/`, 'AdminUsersAPI.createApiKey');
    const resp = await client.post(`/api/admin/users/${id}/api-keys/`, data);
    return resp.data;
  },
};

// Admin API - API Key Management
export const AdminAPIKeysAPI = {
  list: async (params = {}) => {
    apiDebugger.logCall('GET /api/admin/api-keys/', 'AdminAPIKeysAPI.list');
    const resp = await client.get('/api/admin/api-keys/', { params });
    return resp.data;
  },
  retrieve: async (id) => {
    apiDebugger.logCall(`GET /api/admin/api-keys/${id}/`, 'AdminAPIKeysAPI.retrieve');
    const resp = await client.get(`/api/admin/api-keys/${id}/`);
    return resp.data;
  },
  update: async (id, data) => {
    apiDebugger.logCall(`PUT /api/admin/api-keys/${id}/`, 'AdminAPIKeysAPI.update');
    const resp = await client.put(`/api/admin/api-keys/${id}/`, data);
    return resp.data;
  },
  revoke: async (id) => {
    apiDebugger.logCall(`POST /api/admin/api-keys/${id}/revoke/`, 'AdminAPIKeysAPI.revoke');
    const resp = await client.post(`/api/admin/api-keys/${id}/revoke/`);
    return resp.data;
  },
  delete: async (id) => {
    apiDebugger.logCall(`DELETE /api/admin/api-keys/${id}/`, 'AdminAPIKeysAPI.delete');
    const resp = await client.delete(`/api/admin/api-keys/${id}/`);
    return resp.data;
  },
};

// GCS Credentials API
export const GCSCredentialsAPI = {
  getCredentials: async () => {
    apiDebugger.logCall('GET /api/ingestion/gcs-credentials/my_credential/', 'GCSCredentialsAPI.getCredentials');
    const resp = await client.get('/api/ingestion/gcs-credentials/my_credential/');
    return resp.data;
  },
  regenerate: async () => {
    apiDebugger.logCall('POST /api/ingestion/gcs-credentials/regenerate/', 'GCSCredentialsAPI.regenerate');
    const resp = await client.post('/api/ingestion/gcs-credentials/regenerate/');
    return resp.data;
  },
  download: async () => {
    apiDebugger.logCall('GET /api/ingestion/gcs-credentials/download/', 'GCSCredentialsAPI.download');
    const resp = await client.get('/api/ingestion/gcs-credentials/download/', {
      responseType: 'blob', // Important for file download
    });
    return resp;
  },
  getAccessiblePaths: async () => {
    apiDebugger.logCall('GET /api/ingestion/gcs-credentials/accessible_paths/', 'GCSCredentialsAPI.getAccessiblePaths');
    const resp = await client.get('/api/ingestion/gcs-credentials/accessible_paths/');
    return resp.data;
  },
  downloadForUser: async (userId) => {
    apiDebugger.logCall(`GET /api/ingestion/gcs-credentials/download-for-user/${userId}/`, 'GCSCredentialsAPI.downloadForUser');
    const resp = await client.get(`/api/ingestion/gcs-credentials/download-for-user/${userId}/`, {
      responseType: 'blob', // Important for file download
    });
    return resp;
  },
};
